'use client'

import { useState, useMemo, useEffect } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'

// =============================================================================
// TYPES
// =============================================================================

type Persona = 'collector' | 'advisor' | 'gallery' | 'institution' | 'developer' | 'agent'
type BaseTier = 'individual' | 'institutional'

interface Config {
  baseTier: BaseTier
  historyDepth: '10yr' | '30yr' | '50yr'
  alertLimit: '10' | '50' | 'unlimited'
  exportFormats: { csv: boolean; pdf: boolean; json: boolean }
  seats: number
  apiAccess: boolean
  mcpAccess: boolean
  apiCalls: '1k' | '5k' | '10k' | '50k' | 'unlimited'
  streamingFeeds: boolean
  webhooks: boolean
  whiteLabel: boolean
  customIntegrations: boolean
  dedicatedSupport: boolean
  slaGuarantee: boolean
}

// =============================================================================
// PRICING DATA
// =============================================================================

const basePricing = {
  individual: 149,
  institutional: 399,
}

const addOnPricing = {
  // History depth (from 10yr base)
  history30yr: { individual: 25, institutional: 0 },
  history50yr: { individual: 50, institutional: 0 },

  // Alerts (from 10 base)
  alerts50: { individual: 20, institutional: 0 },
  alertsUnlimited: { individual: 40, institutional: 0 },

  // Exports
  exportPdf: { individual: 15, institutional: 0 },
  exportJson: { individual: 25, institutional: 0 },

  // Seats (per additional seat)
  seatPrice: { individual: 0, institutional: 29 },

  // API/Machine
  apiAccess: { individual: 75, institutional: 0 },
  mcpAccess: { individual: 75, institutional: 0 },
  apiCalls5k: { individual: 50, institutional: 0 },
  apiCalls10k: { individual: 100, institutional: 0 },
  apiCalls50k: { individual: 0, institutional: 150 },
  apiCallsUnlimited: { individual: 0, institutional: 0 }, // custom pricing

  // Institutional only
  streamingFeeds: { individual: 0, institutional: 199 },
  webhooks: { individual: 0, institutional: 99 },
  whiteLabel: { individual: 0, institutional: 249 },
  customIntegrations: { individual: 0, institutional: 499 },
  dedicatedSupport: { individual: 0, institutional: 299 },
  slaGuarantee: { individual: 0, institutional: 199 },
}

const personaDefaults: Record<Persona, Partial<Config>> = {
  collector: {
    baseTier: 'individual',
    historyDepth: '30yr',
    alertLimit: '50',
  },
  advisor: {
    baseTier: 'individual',
    historyDepth: '50yr',
    alertLimit: 'unlimited',
    exportFormats: { csv: true, pdf: true, json: false },
  },
  gallery: {
    baseTier: 'individual',
    historyDepth: '50yr',
    alertLimit: 'unlimited',
    seats: 3,
  },
  institution: {
    baseTier: 'institutional',
    seats: 5,
  },
  developer: {
    baseTier: 'institutional',
    apiAccess: true,
    apiCalls: '10k',
  },
  agent: {
    baseTier: 'institutional',
    mcpAccess: true,
    apiAccess: true,
    streamingFeeds: true,
  },
}

const defaultConfig: Config = {
  baseTier: 'individual',
  historyDepth: '10yr',
  alertLimit: '10',
  exportFormats: { csv: true, pdf: false, json: false },
  seats: 1,
  apiAccess: false,
  mcpAccess: false,
  apiCalls: '1k',
  streamingFeeds: false,
  webhooks: false,
  whiteLabel: false,
  customIntegrations: false,
  dedicatedSupport: false,
  slaGuarantee: false,
}

// =============================================================================
// COMPONENT
// =============================================================================

interface Props {
  initialPersona?: Persona
}

export function PricingConfigurator({ initialPersona }: Props) {
  const [persona, setPersona] = useState<Persona | null>(initialPersona || null)
  const [config, setConfig] = useState<Config>(defaultConfig)
  const [showContactModal, setShowContactModal] = useState(false)

  // Apply persona defaults when selected
  useEffect(() => {
    if (persona) {
      const defaults = personaDefaults[persona]
      setConfig(prev => ({ ...prev, ...defaults }))
    }
  }, [persona])

  // Calculate total price
  const { monthlyPrice, isCustomPricing, breakdown } = useMemo(() => {
    let total = basePricing[config.baseTier]
    const items: { label: string; price: number }[] = [
      { label: `${config.baseTier === 'individual' ? 'Individual' : 'Institutional'} base`, price: basePricing[config.baseTier] }
    ]
    const tier = config.baseTier

    // History
    if (config.historyDepth === '30yr' && addOnPricing.history30yr[tier] > 0) {
      total += addOnPricing.history30yr[tier]
      items.push({ label: '30-year history', price: addOnPricing.history30yr[tier] })
    } else if (config.historyDepth === '50yr' && addOnPricing.history50yr[tier] > 0) {
      total += addOnPricing.history50yr[tier]
      items.push({ label: '50-year history', price: addOnPricing.history50yr[tier] })
    }

    // Alerts
    if (config.alertLimit === '50' && addOnPricing.alerts50[tier] > 0) {
      total += addOnPricing.alerts50[tier]
      items.push({ label: '50 alerts/mo', price: addOnPricing.alerts50[tier] })
    } else if (config.alertLimit === 'unlimited' && addOnPricing.alertsUnlimited[tier] > 0) {
      total += addOnPricing.alertsUnlimited[tier]
      items.push({ label: 'Unlimited alerts', price: addOnPricing.alertsUnlimited[tier] })
    }

    // Exports
    if (config.exportFormats.pdf && addOnPricing.exportPdf[tier] > 0) {
      total += addOnPricing.exportPdf[tier]
      items.push({ label: 'PDF exports', price: addOnPricing.exportPdf[tier] })
    }
    if (config.exportFormats.json && addOnPricing.exportJson[tier] > 0) {
      total += addOnPricing.exportJson[tier]
      items.push({ label: 'JSON exports', price: addOnPricing.exportJson[tier] })
    }

    // Seats (institutional only, first seat included)
    if (tier === 'institutional' && config.seats > 1) {
      const seatCost = (config.seats - 1) * addOnPricing.seatPrice[tier]
      total += seatCost
      items.push({ label: `${config.seats - 1} additional seats`, price: seatCost })
    }

    // API/MCP
    if (config.apiAccess && addOnPricing.apiAccess[tier] > 0) {
      total += addOnPricing.apiAccess[tier]
      items.push({ label: 'API access', price: addOnPricing.apiAccess[tier] })
    }
    if (config.mcpAccess && addOnPricing.mcpAccess[tier] > 0) {
      total += addOnPricing.mcpAccess[tier]
      items.push({ label: 'MCP access', price: addOnPricing.mcpAccess[tier] })
    }

    // API calls
    if ((config.apiAccess || config.mcpAccess) && config.apiCalls !== '1k') {
      if (config.apiCalls === '5k' && addOnPricing.apiCalls5k[tier] > 0) {
        total += addOnPricing.apiCalls5k[tier]
        items.push({ label: '5K API calls/mo', price: addOnPricing.apiCalls5k[tier] })
      } else if (config.apiCalls === '10k' && addOnPricing.apiCalls10k[tier] > 0) {
        total += addOnPricing.apiCalls10k[tier]
        items.push({ label: '10K API calls/mo', price: addOnPricing.apiCalls10k[tier] })
      } else if (config.apiCalls === '50k' && addOnPricing.apiCalls50k[tier] > 0) {
        total += addOnPricing.apiCalls50k[tier]
        items.push({ label: '50K API calls/mo', price: addOnPricing.apiCalls50k[tier] })
      }
    }

    // Institutional add-ons
    if (config.streamingFeeds && tier === 'institutional') {
      total += addOnPricing.streamingFeeds[tier]
      items.push({ label: 'Streaming feeds', price: addOnPricing.streamingFeeds[tier] })
    }
    if (config.webhooks && tier === 'institutional') {
      total += addOnPricing.webhooks[tier]
      items.push({ label: 'Webhooks', price: addOnPricing.webhooks[tier] })
    }
    if (config.whiteLabel && tier === 'institutional') {
      total += addOnPricing.whiteLabel[tier]
      items.push({ label: 'White-label reports', price: addOnPricing.whiteLabel[tier] })
    }
    if (config.customIntegrations && tier === 'institutional') {
      total += addOnPricing.customIntegrations[tier]
      items.push({ label: 'Custom integrations', price: addOnPricing.customIntegrations[tier] })
    }
    if (config.dedicatedSupport && tier === 'institutional') {
      total += addOnPricing.dedicatedSupport[tier]
      items.push({ label: 'Dedicated support', price: addOnPricing.dedicatedSupport[tier] })
    }
    if (config.slaGuarantee && tier === 'institutional') {
      total += addOnPricing.slaGuarantee[tier]
      items.push({ label: 'SLA guarantee', price: addOnPricing.slaGuarantee[tier] })
    }

    const needsCustom = config.apiCalls === 'unlimited' || total > 2000

    return { monthlyPrice: total, isCustomPricing: needsCustom, breakdown: items }
  }, [config])

  const updateConfig = <K extends keyof Config>(key: K, value: Config[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Persona Selection */}
      {!persona && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="font-display text-2xl font-light mb-2 text-center">Tell us about yourself</h2>
          <p className="text-text-secondary text-center mb-8">We'll recommend a starting configuration</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {([
              { id: 'collector', label: 'Collector', desc: 'Building a personal collection' },
              { id: 'advisor', label: 'Advisor / Dealer', desc: 'Advising clients on acquisitions' },
              { id: 'gallery', label: 'Gallery', desc: 'Managing artists and inventory' },
              { id: 'institution', label: 'Institution', desc: 'Museum, foundation, or fund' },
              { id: 'developer', label: 'Developer', desc: 'Building with our API' },
              { id: 'agent', label: 'AI Agent', desc: 'MCP or programmatic access' },
            ] as const).map((p) => (
              <button
                key={p.id}
                onClick={() => setPersona(p.id)}
                className="p-5 rounded-2xl text-left transition-all bg-card border border-border hover:border-accent-primary hover:shadow-lg"
              >
                <p className="font-medium mb-1">{p.label}</p>
                <p className="text-xs text-text-tertiary">{p.desc}</p>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Main Configurator */}
      {persona && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Configuration Options */}
          <div className="lg:col-span-2 space-y-8">
            {/* Base Tier */}
            <section className="card p-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-accent-primary text-white text-xs flex items-center justify-center">1</span>
                Choose your base
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {(['individual', 'institutional'] as const).map((tier) => (
                  <button
                    key={tier}
                    onClick={() => updateConfig('baseTier', tier)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      config.baseTier === tier
                        ? 'border-accent-primary bg-accent-primary/5'
                        : 'border-border hover:border-border-hover'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{tier}</span>
                      <span className="text-sm text-text-tertiary">from ${basePricing[tier]}/mo</span>
                    </div>
                    <p className="text-xs text-text-tertiary">
                      {tier === 'individual'
                        ? 'For collectors, advisors, and dealers'
                        : 'For organizations with API/team needs'}
                    </p>
                  </button>
                ))}
              </div>
            </section>

            {/* Data Access */}
            <section className="card p-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-accent-primary text-white text-xs flex items-center justify-center">2</span>
                Data access
              </h3>

              {/* History Depth */}
              <div className="mb-6">
                <label className="text-sm text-text-secondary mb-3 block">Auction history depth</label>
                <div className="flex gap-2">
                  {(['10yr', '30yr', '50yr'] as const).map((depth) => {
                    const price = depth === '30yr'
                      ? addOnPricing.history30yr[config.baseTier]
                      : depth === '50yr'
                        ? addOnPricing.history50yr[config.baseTier]
                        : 0
                    return (
                      <button
                        key={depth}
                        onClick={() => updateConfig('historyDepth', depth)}
                        className={`flex-1 py-3 px-4 rounded-xl border text-sm transition-all ${
                          config.historyDepth === depth
                            ? 'border-accent-primary bg-accent-primary/5 font-medium'
                            : 'border-border hover:border-border-hover'
                        }`}
                      >
                        {depth.replace('yr', ' years')}
                        {price > 0 && <span className="text-text-tertiary ml-1">+${price}</span>}
                        {price === 0 && depth !== '10yr' && <span className="text-positive ml-1">included</span>}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Alerts */}
              <div className="mb-6">
                <label className="text-sm text-text-secondary mb-3 block">Artist alerts per month</label>
                <div className="flex gap-2">
                  {(['10', '50', 'unlimited'] as const).map((limit) => {
                    const price = limit === '50'
                      ? addOnPricing.alerts50[config.baseTier]
                      : limit === 'unlimited'
                        ? addOnPricing.alertsUnlimited[config.baseTier]
                        : 0
                    return (
                      <button
                        key={limit}
                        onClick={() => updateConfig('alertLimit', limit)}
                        className={`flex-1 py-3 px-4 rounded-xl border text-sm transition-all ${
                          config.alertLimit === limit
                            ? 'border-accent-primary bg-accent-primary/5 font-medium'
                            : 'border-border hover:border-border-hover'
                        }`}
                      >
                        {limit === 'unlimited' ? 'Unlimited' : limit}
                        {price > 0 && <span className="text-text-tertiary ml-1">+${price}</span>}
                        {price === 0 && limit !== '10' && <span className="text-positive ml-1">included</span>}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Export Formats */}
              <div>
                <label className="text-sm text-text-secondary mb-3 block">Export formats</label>
                <div className="flex gap-2">
                  {([
                    { id: 'csv', label: 'CSV', price: 0 },
                    { id: 'pdf', label: 'PDF', price: addOnPricing.exportPdf[config.baseTier] },
                    { id: 'json', label: 'JSON', price: addOnPricing.exportJson[config.baseTier] },
                  ] as const).map((format) => (
                    <button
                      key={format.id}
                      onClick={() => updateConfig('exportFormats', {
                        ...config.exportFormats,
                        [format.id]: format.id === 'csv' ? true : !config.exportFormats[format.id]
                      })}
                      disabled={format.id === 'csv'}
                      className={`flex-1 py-3 px-4 rounded-xl border text-sm transition-all ${
                        config.exportFormats[format.id]
                          ? 'border-accent-primary bg-accent-primary/5 font-medium'
                          : 'border-border hover:border-border-hover'
                      } ${format.id === 'csv' ? 'cursor-default' : ''}`}
                    >
                      {format.label}
                      {format.price > 0 && !config.exportFormats[format.id] && (
                        <span className="text-text-tertiary ml-1">+${format.price}</span>
                      )}
                      {format.price === 0 && format.id !== 'csv' && (
                        <span className="text-positive ml-1">included</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Team & Collaboration */}
            {config.baseTier === 'institutional' && (
              <section className="card p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-accent-primary text-white text-xs flex items-center justify-center">3</span>
                  Team
                </h3>
                <div>
                  <label className="text-sm text-text-secondary mb-3 block">
                    Team seats <span className="text-text-tertiary">(first seat included, +$29/additional)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="20"
                      value={config.seats}
                      onChange={(e) => updateConfig('seats', parseInt(e.target.value))}
                      className="flex-1 accent-accent-primary"
                    />
                    <span className="w-16 text-center font-medium">{config.seats} {config.seats === 1 ? 'seat' : 'seats'}</span>
                  </div>
                </div>
              </section>
            )}

            {/* API & Machine Access */}
            <section className="card p-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-accent-primary text-white text-xs flex items-center justify-center">
                  {config.baseTier === 'institutional' ? '4' : '3'}
                </span>
                API & Machine Access
              </h3>

              <div className="space-y-4">
                {/* API Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                  <div>
                    <p className="font-medium">REST API</p>
                    <p className="text-xs text-text-tertiary">Programmatic access to all data</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {addOnPricing.apiAccess[config.baseTier] > 0 ? (
                      <span className="text-sm text-text-tertiary">+${addOnPricing.apiAccess[config.baseTier]}/mo</span>
                    ) : (
                      <span className="text-sm text-positive">Included</span>
                    )}
                    <button
                      onClick={() => updateConfig('apiAccess', !config.apiAccess)}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        config.apiAccess ? 'bg-accent-primary' : 'bg-border'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        config.apiAccess ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* MCP Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                  <div>
                    <p className="font-medium">MCP Protocol</p>
                    <p className="text-xs text-text-tertiary">For AI agents and assistants</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {addOnPricing.mcpAccess[config.baseTier] > 0 ? (
                      <span className="text-sm text-text-tertiary">+${addOnPricing.mcpAccess[config.baseTier]}/mo</span>
                    ) : (
                      <span className="text-sm text-positive">Included</span>
                    )}
                    <button
                      onClick={() => updateConfig('mcpAccess', !config.mcpAccess)}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        config.mcpAccess ? 'bg-accent-primary' : 'bg-border'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        config.mcpAccess ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* API Calls Slider */}
                {(config.apiAccess || config.mcpAccess) && (
                  <div className="p-4 rounded-xl border border-border">
                    <label className="text-sm text-text-secondary mb-3 block">API calls per month</label>
                    <div className="flex gap-2 flex-wrap">
                      {(['1k', '5k', '10k', '50k', 'unlimited'] as const).map((calls) => {
                        const available = config.baseTier === 'institutional' || calls !== '50k' && calls !== 'unlimited'
                        const price = calls === '5k' ? addOnPricing.apiCalls5k[config.baseTier]
                          : calls === '10k' ? addOnPricing.apiCalls10k[config.baseTier]
                          : calls === '50k' ? addOnPricing.apiCalls50k[config.baseTier]
                          : 0
                        return (
                          <button
                            key={calls}
                            onClick={() => available && updateConfig('apiCalls', calls)}
                            disabled={!available}
                            className={`py-2 px-4 rounded-lg border text-sm transition-all ${
                              config.apiCalls === calls
                                ? 'border-accent-primary bg-accent-primary/5 font-medium'
                                : available
                                  ? 'border-border hover:border-border-hover'
                                  : 'border-border opacity-40 cursor-not-allowed'
                            }`}
                          >
                            {calls.toUpperCase()}
                            {price > 0 && <span className="text-text-tertiary ml-1">+${price}</span>}
                            {calls === 'unlimited' && <span className="text-text-tertiary ml-1">custom</span>}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Enterprise Features (Institutional Only) */}
            {config.baseTier === 'institutional' && (
              <section className="card p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-accent-primary text-white text-xs flex items-center justify-center">5</span>
                  Enterprise features
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'streamingFeeds', label: 'Streaming feeds', desc: 'Real-time data updates', price: addOnPricing.streamingFeeds.institutional },
                    { key: 'webhooks', label: 'Webhooks', desc: 'Event notifications', price: addOnPricing.webhooks.institutional },
                    { key: 'whiteLabel', label: 'White-label reports', desc: 'Branded client reports', price: addOnPricing.whiteLabel.institutional },
                    { key: 'customIntegrations', label: 'Custom integrations', desc: 'Bespoke data pipelines', price: addOnPricing.customIntegrations.institutional },
                    { key: 'dedicatedSupport', label: 'Dedicated support', desc: 'Named account manager', price: addOnPricing.dedicatedSupport.institutional },
                    { key: 'slaGuarantee', label: 'SLA guarantee', desc: '99.9% uptime commitment', price: addOnPricing.slaGuarantee.institutional },
                  ].map((feature) => (
                    <button
                      key={feature.key}
                      onClick={() => updateConfig(feature.key as keyof Config, !config[feature.key as keyof Config])}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        config[feature.key as keyof Config]
                          ? 'border-accent-primary bg-accent-primary/5'
                          : 'border-border hover:border-border-hover'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{feature.label}</span>
                        <span className="text-xs text-text-tertiary">+${feature.price}/mo</span>
                      </div>
                      <p className="text-xs text-text-tertiary">{feature.desc}</p>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right: Price Summary (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <motion.div
                layout
                className="card p-6"
                style={{ background: 'var(--hero-ground)' }}
              >
                <h3 className="font-display text-xl font-light mb-4" style={{ color: 'var(--accent-cream)' }}>
                  Your plan
                </h3>

                {/* Price */}
                <div className="mb-6">
                  {isCustomPricing ? (
                    <div>
                      <span className="text-3xl font-display font-light" style={{ color: 'var(--accent-cream)' }}>
                        Custom
                      </span>
                      <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        Let's discuss your needs
                      </p>
                    </div>
                  ) : (
                    <div>
                      <span className="text-4xl font-display font-light" style={{ color: 'var(--accent-cream)' }}>
                        ${monthlyPrice}
                      </span>
                      <span className="text-lg" style={{ color: 'rgba(255,255,255,0.6)' }}>/mo</span>
                    </div>
                  )}
                </div>

                {/* Breakdown */}
                <div className="space-y-2 mb-6 pb-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                  {breakdown.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      <span>{item.label}</span>
                      <span>{item.price === 0 && i > 0 ? 'included' : `$${item.price}`}</span>
                    </div>
                  ))}
                </div>

                {/* What's included summary */}
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-wider mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Includes
                  </p>
                  <ul className="space-y-2">
                    {[
                      `${config.historyDepth.replace('yr', '-year')} history`,
                      `${config.alertLimit === 'unlimited' ? 'Unlimited' : config.alertLimit} alerts/mo`,
                      config.apiAccess && 'REST API access',
                      config.mcpAccess && 'MCP Protocol access',
                      config.baseTier === 'institutional' && `${config.seats} team seat${config.seats > 1 ? 's' : ''}`,
                    ].filter(Boolean).map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                        <svg className="w-4 h-4" style={{ color: 'var(--accent-warm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                {isCustomPricing || monthlyPrice > 500 ? (
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="w-full pill py-3 text-center font-medium"
                    style={{ background: 'var(--accent-cream)', color: 'var(--hero-ground)' }}
                  >
                    Talk to us
                  </button>
                ) : (
                  <Link
                    href={`/checkout?plan=${config.baseTier}&price=${monthlyPrice}`}
                    className="w-full pill py-3 text-center font-medium block"
                    style={{ background: 'var(--accent-cream)', color: 'var(--hero-ground)' }}
                  >
                    Continue to checkout
                  </Link>
                )}

                <p className="text-xs text-center mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Cancel anytime. Billed monthly.
                </p>

                {/* Change persona */}
                <button
                  onClick={() => {
                    setPersona(null)
                    setConfig(defaultConfig)
                  }}
                  className="w-full text-xs mt-4 hover:underline"
                  style={{ color: 'rgba(255,255,255,0.5)' }}
                >
                  Start over
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="card p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-display text-2xl font-light mb-2">Let's talk</h3>
              <p className="text-text-secondary mb-6">
                Your configuration needs custom pricing. Tell us about your use case.
              </p>
              <form className="space-y-4">
                <input
                  type="email"
                  placeholder="Work email"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-page text-text-primary placeholder:text-text-tertiary"
                />
                <input
                  type="text"
                  placeholder="Company / Organization"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-page text-text-primary placeholder:text-text-tertiary"
                />
                <textarea
                  placeholder="Tell us about your use case..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-page text-text-primary placeholder:text-text-tertiary resize-none"
                />
                <button
                  type="submit"
                  className="w-full pill py-3 font-medium"
                  style={{ background: 'var(--accent-primary)', color: 'white' }}
                >
                  Request a call
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
